import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Ticket, Check, ShieldCheck, Mail, Heart, ChevronRight } from "lucide-react";
import SafeImg from "@/components/SafeImg";
import { RichText } from "@/components/RichText";
import { usePageSEO, SEO } from "@/lib/seo";
import { useCmsPage, pickTx } from "@/lib/pageCms";

// Fixed bundle catalogue (mirrored on the server — the server is the source of truth
// but we mirror the display copy here so buttons can render before the config lands).
const BUNDLES = [1, 2, 3, 5, 10];
const BUNDLE_PRICES_FALLBACK = { 1: 50, 2: 100, 3: 150, 5: 225, 10: 400 };

const useT = () => {
  const { lang } = useI18n();
  return (da, en, es) => ({ da, en, es })[lang] || da;
};

// Lightweight analytics helper — pushes to window.dataLayer if a GTM-style
// consumer is present, otherwise no-op. NEVER passes PII, emails, phone or
// raffle numbers.
const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event, ...props });
    }
  } catch (_) { /* silent */ }
};

export default function Raffle() {
  const { lang } = useI18n();
  const t = useT();
  const location = useLocation();
  const cms = useCmsPage("raffle");
  usePageSEO(SEO.raffle.title, SEO.raffle.description, { cmsSeo: cms.seo });
  const campaignSec = cms.enabled ? cms.sections.find(s => s.slug === "campaign_intro") : null;
  const heroSec = cms.enabled ? cms.sections.find(s => s.slug === "hero") : null;
  const drawSec = cms.enabled ? cms.sections.find(s => s.slug === "draw_when") : null;
  const [cfg, setCfg] = useState(null);
  const [shirtCfg, setShirtCfg] = useState(null);
  const [f, setF] = useState({ full_name: "", email: "", phone: "", quantity: 1, terms_accepted: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({});
  const [formInView, setFormInView] = useState(false);
  const buyRef = useRef(null);

  useEffect(() => {
    api.get("/raffle/config").then(r => setCfg(r.data)).catch(() => setCfg({ settings: { sales_enabled: false } }));
    api.get("/shirts/config").then(r => setShirtCfg(r.data)).catch(() => {});
  }, []);

  // Anchor scroll — handles both #buy (form) and #charity-auction. Waits for
  // config so the form is guaranteed to be in the DOM, then scrolls smoothly.
  // location.key is included so re-clicking a Link that points at the same
  // URL (e.g. the "RIFA" nav item while already on /raffle#buy) still fires.
  useEffect(() => {
    if (!location.hash || !cfg) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(timer);
  }, [location.hash, location.key, cfg]);

  // Track when the buy section becomes visible so we can hide the sticky bar
  // AND fire a "purchase_form_viewed" analytics event once per visit.
  useEffect(() => {
    if (!buyRef.current || !cfg) return;
    let fired = false;
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        setFormInView(entry.isIntersecting);
        if (entry.isIntersecting && !fired) {
          fired = true;
          track("raffle_purchase_form_viewed");
        }
      });
    }, { threshold: 0.15 });
    io.observe(buyRef.current);
    return () => io.disconnect();
  }, [cfg]);

  const bundlePrices = cfg?.bundle_prices_dkk || BUNDLE_PRICES_FALLBACK;
  const priceFor = (q) => Number(bundlePrices[q] ?? bundlePrices[String(q)] ?? BUNDLE_PRICES_FALLBACK[q]);
  const total = priceFor(f.quantity);
  const available = cfg?.counts?.available ?? 0;
  const soldOut = cfg && available <= 0;

  // Auto-fallback to the largest still-valid bundle if the current selection is
  // no longer available (e.g. only 3 tickets remain, user had selected 5).
  useEffect(() => {
    if (!cfg) return;
    if (available > 0 && f.quantity > available) {
      const largestValid = [...BUNDLES].reverse().find(q => q <= available) || 1;
      setF(x => ({ ...x, quantity: largestValid }));
    }
  }, [available, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const selectBundle = (q) => {
    if (q > available) return;
    set("quantity", q);
    track("raffle_bundle_selected", { quantity: q, bundle_total_dkk: priceFor(q) });
  };

  const scrollToBuy = () => {
    track("raffle_sticky_cta_clicked");
    const el = document.getElementById("buy");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setFieldErr({});
    if (!f.terms_accepted) {
      setFieldErr({ terms: t("Du skal acceptere vilkårene.", "Please accept the terms.", "Debes aceptar los términos.") });
      return;
    }
    if (f.quantity > available) {
      setErr(t(
        `Der er kun ${available} lodder tilbage.`,
        `Only ${available} tickets left.`,
        `Solo quedan ${available} boletos.`
      ));
      return;
    }
    track("raffle_checkout_started", { quantity: f.quantity, bundle_total_dkk: total });
    setBusy(true);
    try {
      const { data } = await api.post("/raffle/checkout", {
        full_name: f.full_name,
        email: f.email,
        phone: f.phone,
        quantity: f.quantity,
        terms_accepted: f.terms_accepted,
        origin_url: window.location.origin,
        language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (ex) {
      track("raffle_checkout_error", { reason: (ex.response?.status || 0) + "" });
      setErr(ex.response?.data?.detail || t("Der opstod en fejl.", "Something went wrong.", "Ha ocurrido un error."));
      setBusy(false);
    }
  };

  const bundleLabel = (q) => t(
    q === 1 ? `1 lod — ${priceFor(q)} kr.` : `${q} lodder — ${priceFor(q)} kr.`,
    q === 1 ? `1 ticket — ${priceFor(q)} DKK` : `${q} tickets — ${priceFor(q)} DKK`,
    q === 1 ? `1 boleto — ${priceFor(q)} DKK` : `${q} boletos — ${priceFor(q)} DKK`
  );

  const savings = { 5: 25, 10: 100 };
  const savingsLabel = (q) => savings[q] ? t(`SPAR ${savings[q]} KR.`, `SAVE ${savings[q]} DKK`, `AHORRA ${savings[q]} DKK`) : "";
  const badge = (q) => q === 5
    ? t("POPULÆR", "POPULAR", "POPULAR")
    : q === 10 ? t("BEDSTE PRIS", "BEST VALUE", "MEJOR PRECIO") : "";

  const payLabel = f.quantity === 1
    ? t(`BETAL ${total} KR. OG FÅ 1 LOD`, `PAY ${total} DKK AND GET 1 TICKET`, `PAGA ${total} DKK Y RECIBE 1 BOLETO`)
    : t(`BETAL ${total} KR. OG FÅ ${f.quantity} LODDER`, `PAY ${total} DKK AND GET ${f.quantity} TICKETS`, `PAGA ${total} DKK Y RECIBE ${f.quantity} BOLETOS`);

  // Featured prizes — top 3 published prizes by value_dkk (fallback: display order).
  const featuredPrizes = useMemo(() => {
    const list = (cfg?.prizes || []).slice();
    if (list.length === 0) return [];
    const scored = list.map(p => ({ p, v: Number(p.value_dkk) || 0 }));
    scored.sort((a, b) => b.v - a.v || (a.p.order || 0) - (b.p.order || 0));
    const seen = new Set();
    const out = [];
    for (const { p } of scored) {
      const key = (p.title?.en || p.title?.da || p.id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
      if (out.length >= 3) break;
    }
    return out;
  }, [cfg]);

  const stickyLabel = t("KØB LODDER · FRA 50 KR.", "BUY RAFFLE TICKETS · FROM 50 DKK", "COMPRAR BOLETOS · DESDE 50 DKK");
  // Raffle content renders only while raffle sales are on in the CMS.
  const raffleOn = !!cfg && cfg.settings?.sales_enabled !== false;
  return (
    <div className="max-editorial container-x py-8 md:py-16 pb-32 md:pb-16">
      {raffleOn && (<>
      {/* ─── CAMPAIGN INTRO (approved copy, CMS-driven) ─────────────── */}
      <section data-testid="raffle-campaign-intro" className="max-w-3xl">
        <div className="label-mono text-terracotta" data-testid="raffle-campaign-eyebrow">
          {pickTx(campaignSec?.eyebrow, lang) || t(
            "TRIN 2/2: LODTRÆKNING OG T-SHIRTS",
            "STEP 2/2: RAFFLE & T-SHIRTS",
            "PASO 2/2: RIFA Y CAMISETAS"
          )}
        </div>
        <h1 data-testid="raffle-campaign-title" className="font-editorial text-3xl md:text-6xl mt-3 tracking-tighter leading-[1.05]">
          {pickTx(campaignSec?.headline, lang) || t(
            "Hjælp os med at nå vores mål om at donere 50.000 DKK.",
            "Help us reach our 50,000 DKK donation goal.",
            "Ayúdanos a alcanzar nuestra meta de donar 50.000 DKK."
          )}
        </h1>
        <div className="mt-5 md:mt-7 text-ink/85 text-base md:text-lg leading-relaxed" data-testid="raffle-campaign-body">
          <RichText text={pickTx(campaignSec?.body, lang) || t(
            "Vi har allerede doneret 35.000 DKK til RENACE Ávila og mangler nu kun 15.000 DKK for at nå vores mål.\n\nDeltag i DANAmarks lodtrækning, eller køb en af vores T-shirts. Hvert køb bringer os tættere på målet og hjælper os med fortsat at skabe forbindelser mellem Danmark og Spanien.\n\nDer findes kun 300 lodnumre, og mange har allerede fået en ejer. Hvert nummer koster 50 DKK og kan være et vindernummer. Lodtrækningen sendes live på Instagram den 25. oktober.\n\nT-shirts koster 150 DKK, og hvert køb bidrager også direkte til, at vi når målet.",
            "We have already donated 35,000 DKK to RENACE Ávila, and we are now only 15,000 DKK away from our goal.\n\nEnter the DANAmark raffle or buy one of our T-shirts. Every purchase brings us closer to the goal and helps us continue building connections between Denmark and Spain.\n\nThere are only 300 raffle numbers, and many already have an owner. Each number costs 50 DKK and could be a winner. The draw will take place live on Instagram on 25 October.\n\nThe T-shirts cost 150 DKK, and each purchase also contributes directly towards reaching the goal.",
            "Ya hemos donado 35.000 DKK a RENACE Ávila y solo nos faltan 15.000 DKK para alcanzar nuestro objetivo.\n\nParticipa en la rifa de DANAmark o compra una de nuestras camisetas. Cada compra nos acerca a la meta y nos ayuda a seguir construyendo conexiones entre Dinamarca y España.\n\nSolo existen 300 números para la rifa y muchos ya tienen dueño. Cada número cuesta 50 DKK y puede tener premio. El sorteo se celebrará en directo en Instagram el 25 de octubre.\n\nLas camisetas cuestan 150 DKK y su compra también contribuye directamente a alcanzar la meta."
          )} gapClassName="mt-4" />
        </div>
      </section>

      {/* ─── RAFFLE BLOCK — 50 DKK ──────────────────────────────────── */}
      <section id="raffle-block" className="mt-12 md:mt-16 max-w-3xl" data-testid="raffle-block">
        <div className="label-mono text-terracotta">
          {t("LODTRÆKNING · 50 KR.", "RAFFLE · 50 DKK", "RIFA · 50 DKK")}
        </div>
        <h2 data-testid="raffle-title" className="font-editorial text-2xl md:text-4xl mt-2 md:mt-3 tracking-tighter leading-[1.05]">
          {pickTx(heroSec?.headline, lang) || t("VIND FANTASTISKE PRÆMIER. STØT EN GOD SAG.",
             "WIN AMAZING PRIZES.",
             "GANA PREMIOS INCREÍBLES.")}
        </h2>
        <div className="mt-4 md:mt-5 text-ink/80 text-base md:text-lg leading-relaxed" data-testid="raffle-intro">
          <RichText text={pickTx(heroSec?.body, lang) || t(
            "Lodder koster 50 kr. Vælg dine lodder, betal sikkert og modtag dine unikke numre på e-mail. Der er kun 300 lodder.",
            "Raffle tickets cost 50 DKK. Choose your tickets, pay securely and receive your unique numbers by email. Only 300 tickets are available.",
            "Los boletos cuestan 50 DKK. Elige tus boletos, paga de forma segura y recibe tus números únicos por email. Solo hay 300 boletos."
          )} gapClassName="mt-4" />
        </div>

        <div data-testid="raffle-draw-when" className="mt-5 border-l-2 border-terracotta pl-4 py-1 bg-terracotta/5 rounded-r">
          <div className="font-editorial text-base md:text-xl text-ink leading-snug whitespace-pre-line">
            {t(
              "Lodderne sælges nu. Live-lodtrækningen finder sted under Fællesskabsmødet den 25. oktober 2026 kl. 17.00 (dansk tid). Praktiske detaljer deles privat med bekræftede deltagere.",
              "Tickets are on sale now. The live raffle draw takes place during the Community Meeting on 25 October 2026 at 17:00 (Copenhagen time). Practical details are shared privately with confirmed attendees.",
              "Los boletos ya están a la venta. El sorteo en directo se celebrará durante el Encuentro comunitario el 25 de octubre de 2026 a las 17:00 (hora de Copenhague). Los detalles prácticos se comparten en privado con asistentes confirmados."
            )}
          </div>
        </div>

        {/* ─── Gathering countdown ──────────────────────────────────── */}
        <GatheringCountdown lang={lang} />

        {/* ─── Availability counter ─────────────────────────────────── */}
        <div className="mt-6 flex items-center justify-between gap-3 border border-line rounded-xl px-4 py-3 bg-white"
             data-testid="raffle-availability">
          <div className="flex items-center gap-3">
            <Ticket className="text-terracotta shrink-0" size={20} />
            <div>
              <div className="font-editorial text-lg md:text-xl leading-tight" data-testid="raffle-remaining">
                {cfg ? t(
                  `Kun ${available} ud af 300 lodder tilbage`,
                  `Only ${available} of 300 tickets left`,
                  `Solo quedan ${available} de 300 boletos`
                ) : t("Indlæser…", "Loading…", "Cargando…")}
              </div>
              {cfg && (
                <div className="mt-1 h-1.5 rounded-full bg-line/40 overflow-hidden" aria-hidden>
                  <div className="h-full bg-terracotta transition-all"
                       style={{ width: `${Math.max(0, Math.min(100, (available / 300) * 100))}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {soldOut && (
          <div data-testid="raffle-sold-out"
               className="mt-6 border border-terracotta bg-terracotta/5 text-terracotta p-4 text-center font-editorial text-2xl">
            {t("Udsolgt", "Sold out", "Agotado")}
          </div>
        )}
      </section>

      {/* ─── FEATURED PRIZES ─────────────────────────────────────── */}
      {featuredPrizes.length > 0 && (
        <section className="mt-10 md:mt-14" data-testid="raffle-featured-prizes">
          <div className="label-mono text-terracotta">{t("HØJDEPUNKTER", "FEATURED PRIZES", "PREMIOS DESTACADOS")}</div>
          <h2 className="font-editorial text-2xl md:text-3xl mt-2 tracking-tighter">
            {t("Hvad kan du vinde?", "What can you win?", "¿Qué puedes ganar?")}
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredPrizes.map(p => {
              const link = (p.link || "").trim();
              const CardTag = link ? "a" : "article";
              const linkProps = link ? { href: link, target: "_blank", rel: "noreferrer" } : {};
              return (
                <CardTag key={p.id} {...linkProps}
                  className={`border border-line rounded-xl bg-white overflow-hidden block ${link ? "hover:border-terracotta transition-colors" : ""}`}
                  data-testid={`raffle-featured-${p.id}`}>
                  <SafeImg src={p.image} alt={p.image_alt?.[lang] || ""}
                           name={p.title?.[lang] || p.title?.en || p.donor || ""}
                           className="w-full h-32 md:h-40 object-cover"
                           fallbackClassName="w-full h-32 md:h-40" />
                  <div className="p-4">
                    <div className="label-mono text-ink/50 text-[10px]">{p.donor}</div>
                    <h3 className="mt-1 font-editorial text-base md:text-lg leading-snug">{p.title?.[lang] || p.title?.en}</h3>
                    {p.value_dkk ? (
                      <div className="mt-2 label-mono text-terracotta text-[11px]">
                        {t("Værdi", "Value", "Valor")}: {Number(p.value_dkk).toLocaleString("da-DK")} DKK
                      </div>
                    ) : null}
                  </div>
                </CardTag>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── PURCHASE FORM ───────────────────────────────────────── */}
      <div ref={buyRef} id="buy" className="scroll-mt-24 mt-12 md:mt-16 max-w-3xl">
        {!soldOut && cfg?.settings?.sales_enabled !== false && cfg && (
          <form onSubmit={submit} data-testid="raffle-form" className="space-y-6 md:space-y-8">
            <div>
              <div className="label-mono text-terracotta">{t("KØB LODDER", "BUY TICKETS", "COMPRAR BOLETOS")}</div>
              <h2 className="font-editorial text-2xl md:text-3xl mt-2 tracking-tighter">
                {t("Vælg din pakke", "Choose your bundle", "Elige tu pack")}
              </h2>
            </div>

            <section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {BUNDLES.map(q => {
                  const disabled = q > available;
                  const selected = f.quantity === q && !disabled;
                  const b = badge(q);
                  return (
                    <button key={q} type="button" data-testid={`raffle-bundle-${q}`}
                            onClick={() => selectBundle(q)}
                            disabled={disabled}
                            aria-pressed={selected}
                            className={`relative text-left px-4 py-3.5 rounded-xl border transition-all min-h-[64px]
                                       ${selected
                                         ? "border-terracotta bg-terracotta/5 ring-1 ring-terracotta"
                                         : disabled
                                           ? "border-line/60 opacity-40 cursor-not-allowed"
                                           : "border-line hover:border-ink"}`}>
                      {b && (
                        <span data-testid={`raffle-badge-${q}`}
                              className="absolute -top-2 right-3 bg-terracotta text-white text-[9px] tracking-widest px-2 py-0.5 rounded-full">
                          {b}
                        </span>
                      )}
                      <div className="flex items-baseline justify-between gap-3">
                        <div>
                          <div className={`font-editorial text-lg md:text-xl leading-tight ${selected ? "text-terracotta" : ""}`}>
                            {bundleLabel(q)}
                          </div>
                          {savingsLabel(q) && (
                            <div className="mt-0.5 label-mono text-terracotta text-[10px] tracking-widest"
                                 data-testid={`raffle-savings-${q}`}>
                              {savingsLabel(q)}
                            </div>
                          )}
                        </div>
                        {selected && <Check size={18} className="text-terracotta shrink-0 mt-1" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              {available > 0 && available < 10 && (
                <div className="mt-3 text-xs text-ink/60 italic" data-testid="raffle-low-stock">
                  {t(
                    `Kun ${available} lodder tilbage — nogle pakker kan være utilgængelige.`,
                    `Only ${available} tickets left — some bundles may be unavailable.`,
                    `Solo quedan ${available} boletos — algunos packs pueden no estar disponibles.`
                  )}
                </div>
              )}
            </section>

            <section className="space-y-4">
              <Field label={t("Fulde navn", "Full name", "Nombre completo")}
                     testid="raffle-name" v={f.full_name} on={v => set("full_name", v)}
                     autoComplete="name" required />
              <Field label={t("E-mail", "Email", "Email")} testid="raffle-email"
                     type="email" inputMode="email" autoComplete="email"
                     v={f.email} on={v => set("email", v)} required />
              <Field label={t("Telefon (valgfrit)", "Telephone (optional)", "Teléfono (opcional)")}
                     testid="raffle-phone" type="tel" inputMode="tel" autoComplete="tel"
                     v={f.phone} on={v => set("phone", v)} muted />
            </section>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" data-testid="raffle-terms" checked={f.terms_accepted}
                     onChange={e => set("terms_accepted", e.target.checked)} required className="mt-1" />
              <span>{t(
                "Jeg accepterer lodtrækningsvilkårene og privatlivspolitikken.",
                "I accept the raffle terms and the privacy policy.",
                "Acepto los términos de la rifa y la política de privacidad."
              )}{" "}
                <Link to="/legal/raffle-terms" className="underline" data-testid="raffle-terms-link">
                  {t("Læs vilkårene", "Read terms", "Leer términos")}
                </Link>
              </span>
            </label>
            {fieldErr.terms && <div className="text-sm text-red-600" data-testid="raffle-terms-error">{fieldErr.terms}</div>}

            <div className="flex flex-wrap gap-4 text-xs text-ink/70" data-testid="raffle-reassurance">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-terracotta" />
                {t("Sikker betaling", "Secure payment", "Pago seguro")}</span>
              <span className="inline-flex items-center gap-1.5"><Mail size={14} className="text-terracotta" />
                {t("Dine numre sendes på e-mail", "Your numbers arrive by email", "Recibirás tus números por email")}</span>
              <span className="inline-flex items-center gap-1.5"><Heart size={14} className="text-terracotta" />
                {t("Hvert lod støtter RENACE Ávila", "Every ticket supports RENACE Ávila", "Cada boleto apoya a RENACE Ávila")}</span>
            </div>

            {err && <div className="text-sm text-red-600" data-testid="raffle-error">{err}</div>}

            <button type="submit" disabled={busy || soldOut}
                    data-testid="raffle-submit"
                    className="w-full bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-6 py-4 text-base md:text-lg tracking-wide rounded-full font-editorial">
              {busy ? "…" : payLabel}
            </button>
          </form>
        )}
      </div>
      </>)}

      {/* ─── T-SHIRT BLOCK — 150 DKK ─────────────────────────────── */}
      {cfg && <TShirtBlock shirtCfg={shirtCfg} lang={lang} t={t} standalone={!raffleOn} />}

      {raffleOn && (<>
      {/* ─── MORE PRIZES ──────────────────────────────────────────── */}
      <section className="mt-16 md:mt-20 border-t border-line pt-10 md:pt-12 max-w-3xl">
        <div className="label-mono text-terracotta">{t("PRÆMIER", "PRIZES", "PREMIOS")}</div>
        <h2 className="font-editorial text-2xl md:text-4xl mt-2 md:mt-3 tracking-tighter">
          {t("Flere præmier", "More prizes", "Más premios")}
        </h2>
        <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-6" data-testid="raffle-prizes">
          {(() => {
            // Dedup — featured prizes shown above never repeat here.
            const featuredIds = new Set(featuredPrizes.map(p => p.id));
            return (cfg?.prizes || []).filter(p => !featuredIds.has(p.id));
          })().map(p => {
            const link = (p.link || "").trim();
            const CardTag = link ? "a" : "article";
            const linkProps = link ? { href: link, target: "_blank", rel: "noreferrer" } : {};
            return (
              <CardTag key={p.id} {...linkProps}
                className={`border border-line p-5 md:p-6 rounded-xl bg-white block ${link ? "hover:border-terracotta transition-colors" : ""}`}
                data-testid={`raffle-prize-${p.id}`}>
                <SafeImg src={p.image} alt={p.image_alt?.[lang] || ""}
                         name={p.title?.[lang] || p.title?.en || p.donor || ""}
                         className="w-full h-36 md:h-40 object-cover rounded mb-3"
                         fallbackClassName="w-full h-36 md:h-40 mb-3" />
                <div className="label-mono text-ink/50 text-[10px]">{p.donor}</div>
                <h3 className="mt-1 font-editorial text-lg md:text-xl leading-tight">{p.title?.[lang] || p.title?.en}</h3>
                {p.description?.[lang] && <p className="mt-2 text-sm text-ink/70">{p.description[lang]}</p>}
                <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                  {p.value_dkk ? (
                    <span className="label-mono text-terracotta" data-testid={`raffle-prize-value-${p.id}`}>
                      {t("Værdi", "Value", "Valor")}: {Number(p.value_dkk).toLocaleString("da-DK")} DKK
                    </span>
                  ) : <span />}
                  {link && (
                    <span className="text-ink/50 label-mono inline-flex items-center gap-1">
                      {t("Besøg", "Visit", "Visitar")} <ChevronRight size={12} />
                    </span>
                  )}
                </div>
              </CardTag>
            );
          })}
          {(cfg?.prizes || []).length === 0 && (
            <p className="text-ink/50 text-sm md:col-span-2">{t("Der er endnu ingen præmier publiceret.", "No prizes published yet.", "Aún no hay premios publicados.")}</p>
          )}
        </div>
      </section>

      {/* ─── STICKY MOBILE CTA ───────────────────────────────────── */}
      {cfg && !soldOut && !formInView && (
        <div className="md:hidden fixed left-0 right-0 bottom-0 z-40 border-t border-line bg-white/95 backdrop-blur px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] shadow-[0_-6px_20px_-8px_rgba(0,0,0,.15)]">
          <button type="button" onClick={scrollToBuy} data-testid="raffle-sticky-cta"
                  className="w-full bg-terracotta hover:bg-terracotta-dark text-white px-6 py-3.5 text-sm tracking-wide rounded-full font-editorial">
            {stickyLabel}
          </button>
        </div>
      )}
      </>)}
    </div>
  );
}

function Field({ label, v, on, type = "text", inputMode, autoComplete, testid, required, muted }) {
  return (
    <div>
      <label className={`label-mono ${muted ? "text-ink/50" : "text-ink/60"}`}>{label}</label>
      <input data-testid={testid} type={type} inputMode={inputMode} autoComplete={autoComplete}
             required={required} value={v}
             onChange={e => on(e.target.value)}
             className={`mt-2 w-full border ${muted ? "border-line/70" : "border-line"} focus:border-terracotta outline-none px-4 py-3 bg-transparent rounded-md`} />
    </div>
  );
}

export function RaffleThankYou() {
  const { lang } = useI18n();
  const t = useT();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("loading");
  const [tickets, setTickets] = useState([]);
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let n = 0;
    const poll = async () => {
      n++;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          try {
            const c = await api.get("/raffle/config");
            setTickets(c.data.counts);
          } catch (e) {}
          try { track("raffle_purchase_completed"); } catch (e) {}
          setStatus("paid"); return;
        }
      } catch (e) {}
      if (n < 20) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-editorial container-x py-24 text-center max-w-2xl">
      {status === "paid" ? (
        <>
          <div className="mx-auto w-14 h-14 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Ticket size={28} />
          </div>
          <h1 data-testid="raffle-thanks-title" className="font-editorial text-4xl md:text-5xl mt-6 tracking-tighter">
            {t("Tak for din støtte!", "Thank you for your support!", "¡Gracias por tu apoyo!")}
          </h1>
          <p className="mt-6 text-ink/75 text-lg" data-testid="raffle-thanks-body">
            {t(
              "Tak for din støtte. Live-lodtrækningen finder sted under Fællesskabsmødet den 25. oktober 2026 kl. 17.00 (dansk tid). Du modtager en bekræftelse på mail med dine numre inden for få minutter.",
              "Thank you for your support. The live raffle draw takes place during the Community Meeting on 25 October 2026 at 17:00 (Copenhagen time). You will receive an email with your ticket numbers within a few minutes.",
              "Gracias por tu apoyo. El sorteo en directo se celebrará durante el Encuentro comunitario el 25 de octubre de 2026 a las 17:00 (hora de Copenhague). Recibirás un email con tus números en unos minutos."
            )}
          </p>
          <p className="mt-4 text-terracotta font-editorial text-2xl">
            {t("Sammen viser vi, at fællesskab ikke kender grænser.", "Together, we show that connection knows no borders.", "Juntos demostramos que la conexión no conoce fronteras.")}
          </p>
          <Link to="/" className="mt-10 inline-block border border-ink px-6 py-3 text-sm tracking-wide hover:bg-ink hover:text-white transition-colors rounded-full">
            {t("Tilbage til forsiden", "Back to home", "Volver al inicio")}
          </Link>
        </>
      ) : status === "loading" ? (
        <p className="text-ink/60">…</p>
      ) : (
        <p className="text-red-600">{t("Betalingen kunne ikke bekræftes. Kontakt os venligst.", "Payment could not be confirmed. Please contact us.", "No se ha podido confirmar el pago. Contáctanos, por favor.")}</p>
      )}
    </div>
  );
}


// ─── Gathering countdown (public — brand-styled) ────────────────────────
// Counts down to 25 October 2026 17:00 Europe/Copenhagen (UTC+2, CEST) — the
// confirmed Live Raffle Draw time. Stops at zero after the target time.
function GatheringCountdown({ lang }) {
  const target = React.useMemo(() => new Date("2026-10-25T17:00:00+02:00").getTime(), []);
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const stopped = diff === 0;
  const label = ({ en: "Live raffle draw starts in", da: "Live-lodtrækning begynder om", es: "El sorteo en directo empieza en" })[lang] || "Live raffle draw starts in";
  const labelOver = ({ en: "The live raffle draw has started", da: "Live-lodtrækningen er begyndt", es: "El sorteo en directo ha comenzado" })[lang] || "The live raffle draw has started";
  const dayLbl = ({ en: "DAYS", da: "DAGE", es: "DÍAS" })[lang] || "DAYS";
  const hrLbl  = ({ en: "HRS",  da: "TIMER", es: "HRS" })[lang] || "HRS";
  const minLbl = ({ en: "MIN",  da: "MIN",  es: "MIN" })[lang] || "MIN";
  return (
    <div data-testid="raffle-gathering-countdown" className="mt-6 border border-brand-dark/15 bg-white rounded-xl px-5 py-5">
      <div className="label-mono text-brand-coral text-[10px] tracking-[0.22em]" data-testid="gathering-countdown-label">
        {stopped ? labelOver : label}
      </div>
      {!stopped && (
        <div className="mt-3 flex items-baseline gap-5 md:gap-7" aria-hidden>
          <CountCell value={days} label={dayLbl} />
          <CountCell value={String(hours).padStart(2, "0")} label={hrLbl} />
          <CountCell value={String(mins).padStart(2, "0")} label={minLbl} />
        </div>
      )}
      <div className="mt-3 text-[13px] text-brand-dark/70">
        {({ en: "The raffle draw takes place live during the Community Meeting.",
            da: "Lotteritrækningen finder sted live under Fællesskabsmødet.",
            es: "El sorteo de la rifa se celebrará en directo durante el Encuentro comunitario." })[lang]}
      </div>
    </div>
  );
}

function CountCell({ value, label }) {
  return (
    <div>
      <div className="font-editorial text-3xl md:text-4xl tracking-tighter tabular-nums text-brand-dark">{value}</div>
      <div className="label-mono text-brand-dark/50 text-[9px] tracking-widest mt-1">{label}</div>
    </div>
  );
}


// ─── T-SHIRT BLOCK (inline on /raffle) ──────────────────────────────────
// Uses the existing safe /shirts/checkout flow — a separate Stripe
// Checkout session per product. Keeps the raffle purchase flow untouched.
// Image is only rendered when a URL is uploaded via the CMS; otherwise no
// placeholder frame is shown publicly.
function TShirtBlock({ shirtCfg, lang, t, standalone }) {
  const [counts, setCounts] = React.useState({});
  const [form, setForm] = React.useState({ full_name: "", email: "", phone: "" });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const unit = shirtCfg?.unit_price_dkk || 150;
  const availableSizes = shirtCfg?.sizes_available || shirtCfg?.sizes_adult || ["XS", "S", "M", "L", "XL", "XXL"];
  const product = shirtCfg?.product || {};
  const title = (product.title && (product.title[lang] || product.title.en || product.title.da)) || "";
  const description = (product.description && (product.description[lang] || product.description.en || product.description.da)) || "";
  // Prefer the ordered gallery; fall back to the legacy single image field.
  const galleryRaw = Array.isArray(product.images) ? product.images : [];
  const gallery = galleryRaw.filter(i => i && (i.url || "").trim() && i.show !== false);
  const legacyImage = (product.image || "").trim();
  const effectiveImages = gallery.length > 0
    ? gallery.map(i => ({ url: i.url, alt: (i.alt && (i.alt[lang] || i.alt.en)) || "" }))
    : (legacyImage ? [{ url: legacyImage, alt: (product.image_alt && (product.image_alt[lang] || product.image_alt.en)) || "" }] : []);
  const [activeImg, setActiveImg] = React.useState(0);
  const hasImage = effectiveImages.length > 0;

  const inc = (s) => setCounts(c => ({ ...c, [s]: (c[s] || 0) + 1 }));
  const dec = (s) => setCounts(c => ({ ...c, [s]: Math.max(0, (c[s] || 0) - 1) }));
  const totalQty = React.useMemo(() => Object.values(counts).reduce((a, b) => a + Number(b || 0), 0), [counts]);
  const totalDkk = unit * totalQty;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (totalQty < 1) {
      setErr(t("Vælg mindst én T-shirt.", "Select at least one T-shirt.", "Selecciona al menos una camiseta."));
      return;
    }
    if (!form.full_name.trim()) {
      setErr(t("Skriv dit fulde navn.", "Please enter your full name.", "Escribe tu nombre completo."));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setErr(t("Skriv en gyldig e-mailadresse.", "Please enter a valid email address.", "Escribe un email válido."));
      return;
    }
    const shirt_sizes = [];
    Object.entries(counts).forEach(([s, q]) => { for (let i = 0; i < Number(q || 0); i++) shirt_sizes.push(s); });
    setBusy(true);
    try {
      const { data } = await api.post("/shirts/checkout", {
        ...form,
        shirt_sizes,
        origin_url: window.location.origin,
        language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      const status = ex.response?.status || 0;
      setErr(status >= 400 && status < 500
        ? t("Tjek dine oplysninger, og prøv igen.", "Please check your details and try again.", "Revisa tus datos e inténtalo de nuevo.")
        : (lang === "en" && typeof detail === "string" ? detail : t("Der opstod en fejl.", "Something went wrong.", "Ha ocurrido un error.")));
      setBusy(false);
    }
  };

  const Heading = standalone ? "h1" : "h2";
  return (
    <section id={standalone ? "buy" : "tshirts"} className={`scroll-mt-24 max-w-3xl ${standalone ? "" : "mt-16 md:mt-20 border-t border-line pt-10 md:pt-12"}`} data-testid="tshirt-block">
      <div className="label-mono text-terracotta">
        {t("T-SHIRTS · 150 KR.", "T-SHIRTS · 150 DKK", "CAMISETAS · 150 DKK")}
      </div>
      <Heading className="font-editorial text-2xl md:text-4xl mt-2 md:mt-3 tracking-tighter" data-testid="tshirt-title">
        {title || t("DANAmark T-shirt", "DANAmark T-shirt", "Camiseta DANAmark")}
      </Heading>
      {description && (
        <div className="mt-4 text-ink/80 text-base md:text-lg leading-relaxed" data-testid="tshirt-description">
          <RichText text={description} gapClassName="mt-4" />
        </div>
      )}
      <div className="mt-6 grid md:grid-cols-5 gap-6 md:gap-8">
        {/* Image column — rendered only when an image has been uploaded. */}
        {hasImage ? (
          <div className="md:col-span-2" data-testid="tshirt-image-wrap">
            <img src={effectiveImages[activeImg].url}
                 alt={effectiveImages[activeImg].alt}
                 loading="lazy"
                 className="w-full h-auto rounded-xl border border-line object-cover"
                 data-testid="tshirt-image" />
            {effectiveImages.length > 1 && (
              <div className="mt-2 flex gap-2" data-testid="tshirt-image-thumbs">
                {effectiveImages.map((im, i) => (
                  <button type="button" key={i}
                          onClick={() => setActiveImg(i)}
                          data-testid={`tshirt-image-thumb-${i}`}
                          className={`w-14 h-14 border rounded overflow-hidden ${activeImg === i ? "border-terracotta" : "border-line"}`}>
                    <img src={im.url} alt={im.alt} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
        <form onSubmit={submit} noValidate className={`${hasImage ? "md:col-span-3" : "md:col-span-5"} space-y-5`} data-testid="tshirt-form">
          <div>
            <div className="label-mono text-ink/60 mb-3">
              {t("Vælg størrelse(r)", "Choose size(s)", "Elige talla(s)")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {availableSizes.map((s) => (
                <div key={s} data-testid={`tshirt-size-${s}`}
                     className="border border-line rounded-lg px-3 py-2.5 flex items-center justify-between gap-2">
                  <span className="font-editorial text-base tabular-nums text-ink shrink-0 min-w-[28px]">{s}</span>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => dec(s)} data-testid={`tshirt-dec-${s}`}
                            className="w-7 h-7 rounded-full border border-line hover:border-ink flex items-center justify-center text-ink">
                      −
                    </button>
                    <span data-testid={`tshirt-count-${s}`} className="w-5 text-center tabular-nums text-sm text-ink">{counts[s] || 0}</span>
                    <button type="button" onClick={() => inc(s)} data-testid={`tshirt-inc-${s}`}
                            className="w-7 h-7 rounded-full bg-ink text-white hover:bg-terracotta flex items-center justify-center">
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Field label={t("Fulde navn", "Full name", "Nombre completo")}
                   testid="tshirt-name" v={form.full_name}
                   on={(v) => setForm(f => ({ ...f, full_name: v }))}
                   autoComplete="name" required />
            <Field label={t("E-mail", "Email", "Email")} testid="tshirt-email" type="email"
                   inputMode="email" autoComplete="email"
                   v={form.email} on={(v) => setForm(f => ({ ...f, email: v }))} required />
            <Field label={t("Telefon (valgfrit)", "Telephone (optional)", "Teléfono (opcional)")}
                   testid="tshirt-phone" type="tel" inputMode="tel" autoComplete="tel"
                   v={form.phone} on={(v) => setForm(f => ({ ...f, phone: v }))} muted />
          </div>

          {err && <div className="text-sm text-red-600" data-testid="tshirt-error">{err}</div>}

          <div className="flex items-center justify-between border-t border-line pt-4 flex-wrap gap-3">
            <div>
              <div className="label-mono text-ink/50">
                {t("Antal", "Quantity", "Cantidad")}: <span data-testid="tshirt-qty">{totalQty}</span>
              </div>
              <div className="font-editorial text-2xl text-terracotta" data-testid="tshirt-total">{totalDkk} DKK</div>
            </div>
            <button type="submit" disabled={busy || totalQty < 1 || !shirtCfg} data-testid="tshirt-submit"
                    className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-6 py-3.5 text-sm md:text-base tracking-wide rounded-full font-editorial inline-flex items-center gap-2">
              {busy ? "…" : t(
                `Betal ${totalDkk} DKK og køb T-shirt`,
                `Pay ${totalDkk} DKK and buy T-shirt`,
                `Pagar ${totalDkk} DKK y comprar camiseta`
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
